import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { startPlanCheckout } from "@/lib/planCheckout";
import { PlanChangeDialog } from "@/components/billing/PlanChangeDialog";
import { PlanChangeHistory } from "@/components/billing/PlanChangeHistory";
import { AutoRenewCard } from "@/components/billing/AutoRenewCard";
import { Download, CheckCircle2, AlertTriangle, CreditCard, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const USAGE = { users: "Team users", branches: "Branches", customers: "Customers", pets: "Pets", bookings_per_month: "Bookings / month", services: "Services", products: "Products" };

export default function SaasBilling() {
  const qc = useQueryClient();
  const { refreshMe } = useAuth();
  const [payPlan, setPayPlan] = useState(null);
  const [paying, setPaying] = useState(false);
  const [interval, setBillingInterval] = useState("monthly");
  const { data: cfg } = useQuery({ queryKey: ["plan-config"], queryFn: async () => (await api.get("/saas/plan-change/config")).data });
  const { data: sub, isLoading } = useQuery({ queryKey: ["saas-sub"], queryFn: async () => (await api.get("/saas/subscription")).data });
  const { data: plansData } = useQuery({ queryKey: ["saas-plans"], queryFn: async () => (await api.get("/saas/plans")).data });
  const { data: usage } = useQuery({ queryKey: ["billing-usage"], queryFn: async () => (await api.get("/billing/usage")).data });
  const { data: invoices } = useQuery({ queryKey: ["saas-invoices"], queryFn: async () => (await api.get("/saas/billing/invoices")).data });

  const inval = () => { ["saas-sub", "saas-invoices", "billing-usage", "plan-history", "me"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); refreshMe?.(); };
  const checkout = useMutation({ mutationFn: (b) => api.post("/saas/subscription/checkout", b), onSuccess: (r) => { toast.success(r.data.free ? "Free plan active" : "Checkout started"); inval(); }, onError: (e) => toast.error(apiErr(e)) });
  const downgrade = useMutation({ mutationFn: (b) => api.post("/saas/subscription/downgrade", b), onSuccess: () => { toast.success("Switch to Free scheduled at period end"); inval(); }, onError: (e) => toast.error(apiErr(e)) });
  const cancel = useMutation({ mutationFn: () => api.post("/saas/subscription/cancel"), onSuccess: () => { toast.success("Will cancel at period end"); inval(); }, onError: (e) => toast.error(apiErr(e)) });
  const resume = useMutation({ mutationFn: () => api.post("/saas/subscription/resume"), onSuccess: () => { toast.success("Subscription resumed"); inval(); }, onError: (e) => toast.error(apiErr(e)) });

  useEffect(() => {
    if (!isLoading && window.location.hash === "#plans") document.getElementById("plans")?.scrollIntoView({ behavior: "smooth" });
  }, [isLoading]);
  if (isLoading || !sub) return <Loading />;
  const plans = plansData?.plans || [];
  const rank = { free: 0, starter: 1, growth: 2, multi_branch: 3 };
  const cur = rank[sub.plan_code] ?? 0;
  const paidActive = sub.plan_code !== "free"
    && ["ACTIVE", "CANCEL_AT_PERIOD_END", "PAST_DUE", "GRACE_PERIOD"].includes(sub.status)
    && sub.current_period_end && new Date(sub.current_period_end) > new Date();

  const payForPlan = (plan) => {
    setPaying(true);
    startPlanCheckout({ planCode: plan.code, interval, onResult: ({ state, message, order }) => {
      if (state === "open" || state === "error") { setPaying(false); setPayPlan(null); }
      if (state === "error") toast.error(message);
      if (state === "verifying") toast.message("Verifying payment with Razorpay…");
      if (state === "activated") toast.success(`${order.plan_name} plan is now active`);
      if (state === "paid_pending") toast.message("Payment received — activation is being completed. Check status in Plan change history.");
      if (state === "unverified") toast.error(`${message || "Payment could not be verified"}`);
      if (state === "cancelled") toast.message("Payment cancelled — your plan is unchanged");
      if (state === "failed") toast.error("Payment failed — your plan was not changed");
      if (["activated", "paid_pending", "unverified", "cancelled", "failed"].includes(state)) inval();
      if (state === "activated") refreshMe?.();  // server-verified payment -> trial converted; re-read access
    } });
  };
  const price = (p) => (interval === "annual" ? p.annual_price : p.monthly_price);
  const total = (p) => (interval === "annual" ? p.annual_total : p.monthly_total);

  return (
    <div data-testid="saas-billing-page">
      <PageHeader title="Billing" subtitle="Your Pawkt subscription, usage and invoices" />
      {usage?.internal_business && (
        <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="internal-business-banner">
          Internal business: complimentary unlimited Pawkt CRM. No subscription payment is required. AI, calling and other third-party usage charges still apply.
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-5" data-testid="current-plan-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">Current plan</div>
            <div className="font-display text-2xl font-bold text-slate-900">{sub.plan_name}</div>
            <div className="text-sm text-slate-500">{sub.total_amount > 0 ? `${inr(sub.amount)} + GST = ${inr(sub.total_amount)} / ${sub.billing_interval}` : "₹0 — Free"}</div>
          </div>
          <div className="text-right">
            <span data-testid="sub-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${sub.status === "ACTIVE" || sub.status === "FREE" ? "bg-emerald-100 text-emerald-700" : sub.status === "CANCEL_AT_PERIOD_END" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{sub.status.replace(/_/g, " ")}</span>
            {sub.current_period_end && <div className="mt-1 text-xs text-slate-400">{sub.status === "CANCEL_AT_PERIOD_END" ? "Ends" : "Renews"} {String(sub.current_period_end).slice(0, 10)}</div>}
          </div>
        </div>
        {sub.status === "PENDING_PAYMENT" && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700" data-testid="pending-note">
            <AlertTriangle className="h-4 w-4" /> Payment pending — your paid plan activates only after a successful payment below.
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {sub.status === "CANCEL_AT_PERIOD_END"
            ? <Button onClick={() => resume.mutate()} data-testid="resume-btn" className="bg-emerald-600 text-white">Resume subscription</Button>
            : sub.plan_code !== "free" && <Button variant="outline" onClick={() => cancel.mutate()} data-testid="cancel-btn">Cancel subscription</Button>}
        </div>
      </div>

      <AutoRenewCard sub={sub} interval={interval} onChanged={inval} />

      {(() => {
        const warn = Object.entries(USAGE).map(([k, label]) => {
          const used = usage?.usage?.[k] ?? 0; const lim = usage?.limits?.[k] ?? -1;
          if (lim < 0) return null;
          const pct = (used / Math.max(lim, 1)) * 100;
          if (pct >= 100) return { k, label, used, lim, over: true };
          if (pct >= 80) return { k, label, used, lim, over: false };
          return null;
        }).filter(Boolean);
        if (!warn.length) return null;
        const atLimit = warn.some((w) => w.over);
        return (
          <div data-testid="plan-limit-banner" className={`mb-5 flex flex-wrap items-center gap-3 rounded-xl border p-4 text-sm ${atLimit ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="flex-1">{warn.map((w) => `${w.label} ${w.used}/${w.lim}`).join(" · ")}. {atLimit ? "You've reached a plan limit — upgrade to add more." : "You're approaching a plan limit on your current plan."}</span>
            <a href="#plans" data-testid="banner-upgrade-cta"><Button size="sm" className="bg-slate-900 text-white">Upgrade plan</Button></a>
          </div>
        );
      })()}

      <div className="mb-2 flex items-center justify-between" data-testid="usage-header">
        <h3 className="text-base font-semibold text-slate-800">Usage & limits</h3>
        <span className="text-xs text-slate-500">Enforced on your <b className="capitalize" data-testid="effective-plan">{usage?.plan || sub.plan_code}</b> plan</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-5" data-testid="usage-grid">
        {Object.entries(USAGE).map(([k, label]) => {
          const used = usage?.usage?.[k] ?? 0; const lim = usage?.limits?.[k] ?? -1; const unlimited = lim < 0;
          const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(lim, 1)) * 100));
          return (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`usage-${k}`}>
              <div className="flex justify-between text-sm"><span className="text-slate-600">{label}</span><span className="font-semibold text-slate-800">{used}{unlimited ? "" : ` / ${lim}`}</span></div>
              {!unlimited && <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>}
            </div>
          );
        })}
      </div>

      <div id="plans" className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold text-slate-900">Available plans</h3>
        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 text-sm" role="tablist" data-testid="interval-toggle">
          {["monthly", "annual"].map((i) => (
            <button key={i} role="tab" aria-selected={interval === i} data-testid={`interval-${i}`} onClick={() => setBillingInterval(i)}
              className={`rounded-full px-4 py-1.5 capitalize transition-colors ${interval === i ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{i}</button>
          ))}
        </div>
      </div>
      {cfg && cfg.configured && cfg.checkout_open === false && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="checkout-locked">
          <Lock className="h-4 w-4 shrink-0" /> Online plan purchases are opening soon. Your current plan and access are unchanged.
        </div>
      )}
      {cfg && !cfg.configured && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="payments-unavailable">
          <AlertTriangle className="h-4 w-4 shrink-0" /> Online payment isn't configured yet, so paid plan changes can't be started. Your current plan is unaffected.
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {plans.map((p) => {
          const isCurrent = p.code === sub.plan_code;
          const isUpgrade = (rank[p.code] ?? 0) > cur;
          return (
            <div key={p.code} data-testid={`plan-card-${p.code}`} className={`rounded-xl border p-5 ${isCurrent ? "border-emerald-500 ring-1 ring-emerald-500" : "border-slate-200"}`}>
              <div className="font-semibold text-slate-900">{p.name}</div>
              <div className="mt-1 font-display text-xl font-bold" data-testid={`plan-price-${p.code}`}>{p.code === "free" ? "₹0" : price(p) ? inr(price(p)) : "—"}<span className="text-xs font-normal text-slate-400">{p.code !== "free" && price(p) ? ` /${interval === "annual" ? "yr" : "mo"} + GST` : ""}</span></div>
              <div className="text-xs text-slate-400">{p.code === "free" ? "Free forever" : price(p) ? `${inr(total(p))} incl. ${p.gst_rate}% GST` : `No ${interval} price configured`}</div>
              {isCurrent ? <div className="mt-4 flex items-center gap-1 text-sm font-medium text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Current plan</div>
                : p.code === "free"
                  ? <Button variant="outline" className="mt-4 w-full" data-testid={`switch-free-${p.code}`} onClick={() => (paidActive ? downgrade.mutate({ plan_code: "free", billing_interval: "monthly" }) : checkout.mutate({ plan_code: "free", billing_interval: "monthly" }))}>Switch to Free</Button>
                  : !price(p) || (cfg && (!cfg.configured || cfg.checkout_open === false))
                    ? <Button variant="outline" disabled className="mt-4 w-full" data-testid={`unavailable-${p.code}`}>{!price(p) ? "Not available" : cfg?.checkout_open === false ? "Opening soon" : "Payment unavailable"}</Button>
                  : isUpgrade
                    ? <Button className="mt-4 w-full bg-slate-900 text-white" data-testid={`upgrade-${p.code}`} onClick={() => setPayPlan(p)}>Upgrade — Pay {inr(total(p))}</Button>
                    : paidActive
                      ? <Button variant="outline" disabled className="mt-4 w-full gap-1" data-testid={`locked-${p.code}`} title={`Available after ${String(sub.current_period_end).slice(0, 10)}`}><Lock className="h-3.5 w-3.5" /> Available after {String(sub.current_period_end).slice(0, 10)}</Button>
                      : <Button variant="outline" className="mt-4 w-full" data-testid={`select-${p.code}`} onClick={() => setPayPlan(p)}>Select — Pay {inr(total(p))}</Button>}
            </div>
          );
        })}
      </div>

      <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Plan change history</h3>
      <PlanChangeHistory onChanged={inval} />

      <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Invoices</h3>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        {(invoices?.items?.length) ? (
          <table className="w-full text-sm" data-testid="saas-invoices-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.items.map((iv) => (
                <tr key={iv.id} data-testid={`saas-invoice-${iv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{iv.invoice_number}</td>
                  <td className="px-4 py-3 text-slate-500">{String(iv.created_at).slice(0, 10)}</td>
                  <td className="px-4 py-3 capitalize">{iv.plan_code} · {iv.billing_interval}</td>
                  <td className="px-4 py-3 text-right font-semibold">{inr(iv.total)}</td>
                  <td className="px-4 py-3 capitalize">{iv.status}</td>
                  <td className="px-4 py-3 text-right"><Button variant="outline" size="sm" className="h-7 gap-1 text-xs" data-testid={`saas-invoice-dl-${iv.id}`} onClick={() => window.open(`${api.defaults.baseURL}/saas/billing/invoices/${iv.id}/pdf`, "_blank")}><Download className="h-3 w-3" /> PDF</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="p-8 text-center text-sm text-slate-400"><CreditCard className="mx-auto h-6 w-6 text-slate-300 mb-2" /> No Pawkt invoices yet</div>}
      </div>
      <p className="mt-3 text-xs text-slate-400">These are your Pawkt subscription invoices (Business → Pawkt), separate from your customer invoices.</p>

      {payPlan && <PlanChangeDialog plan={payPlan} interval={interval} busy={paying} onCancel={() => setPayPlan(null)} onPay={() => payForPlan(payPlan)} />}
    </div>
  );
}
